import type { Cadena, FechaDia, Responsable } from '../../negocio/negocio.types';

/**
 * Responsables por tienda (Consulta CM, fase 1). Las reglas de verdad están en
 * la API (`responsables.domain.ts`); aquí sólo lo que la pantalla necesita para
 * no mandar algo que la API va a rechazar y para mostrar el historial.
 */

const ZONA = 'America/Mexico_City';
const SEMILLA = '2000-01-01';
const MESES = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.', 'jul.', 'ago.', 'sep.', 'oct.', 'nov.', 'dic.'];
const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

export function hoyMx(ahora: Date = new Date()): FechaDia {
  return new Intl.DateTimeFormat('en-CA', { timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit' }).format(ahora);
}

export function fechaValida(f: string): boolean {
  if (!FECHA_RE.test(f)) return false;
  const d = new Date(`${f}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === f;
}

export function sumarDias(f: FechaDia, n: number): FechaDia {
  const d = new Date(`${f}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** «2 nov. 2026» */
export function fechaCorta(f: FechaDia): string {
  if (!fechaValida(f)) return f;
  const [a, m, d] = f.split('-').map(Number);
  return `${d} ${MESES[m - 1]} ${a}`;
}

/** Lo que acepta la API: de hoy − 7 a hoy + 90 (hora de México). */
export function rangoVigencia(ahora: Date = new Date()): { min: FechaDia; max: FechaDia; hoy: FechaDia } {
  const hoy = hoyMx(ahora);
  return { min: sumarDias(hoy, -7), max: sumarDias(hoy, 90), hoy };
}

/** `hasta` anterior a `desde`: se corrigió el mismo día y nunca estuvo vigente. */
export const estaAnulada = (r: Pick<Responsable, 'vigenteDesde' | 'vigenteHasta'>) =>
  !!r.vigenteHasta && r.vigenteHasta < r.vigenteDesde;

export function etiquetaVigencia(r: Pick<Responsable, 'vigenteDesde' | 'vigenteHasta'>): string {
  if (estaAnulada(r)) return 'Anulada (se corrigió el mismo día)';
  const desde = r.vigenteDesde === SEMILLA ? null : fechaCorta(r.vigenteDesde);
  if (r.vigenteHasta) return `${desde ?? 'Catálogo'} – ${fechaCorta(r.vigenteHasta)}`;
  return desde ? `Desde ${desde}` : 'Desde el catálogo';
}

export const llaveTienda = (t: { cadena: string; num: number }) => `${t.cadena}-${t.num}`;

const CADENAS = new Set(['TRE', 'VAZ', 'TAF', 'MRH']);

/** `?tiendas=TAF-118,VAZ-1`: la lista que deja una baja. Lo que no es tienda se ignora. */
export function parsearTiendasParam(p: string | null | undefined): string[] {
  if (!p) return [];
  return p
    .split(',')
    .map((x) => x.trim().toUpperCase())
    .filter((x) => {
      const m = /^([A-Z]{3})-(\d+)$/.exec(x);
      return !!m && CADENAS.has(m[1]);
    });
}

export const tiendasParam = (ts: Array<{ cadena: Cadena | string; num: number }>) => ts.map(llaveTienda).join(',');

const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

export interface FiltrosResponsables {
  texto?: string;
  supervisor?: string | null;
  regional?: string | null;
  soloSinResponsable?: boolean;
  tiendas?: string[];
}

export function filtrarResponsables(lista: Responsable[], f: FiltrosResponsables): Responsable[] {
  const q = normalizar((f.texto ?? '').trim());
  const tiendas = f.tiendas && f.tiendas.length ? new Set(f.tiendas) : null;
  return lista.filter((r) => {
    if (tiendas && !tiendas.has(llaveTienda(r))) return false;
    if (f.supervisor && r.supervisor !== f.supervisor) return false;
    if (f.regional && r.regional !== f.regional) return false;
    if (f.soloSinResponsable && r.supervisor && r.regional) return false;
    if (!q) return true;
    const hay = normalizar(`${r.cadena} ${r.num} ${r.tienda} ${r.supervisor ?? ''} ${r.regional ?? ''}`);
    return hay.includes(q);
  });
}

export const NOMBRE_MAX = 80;
export const MOTIVO_MAX = 200;

export function validarReasignacion(
  v: { supervisor: string | null; regional: string | null; vigenteDesde: string; motivo: string },
  rango: { min: FechaDia; max: FechaDia },
): string[] {
  const e: string[] = [];
  if (!fechaValida(v.vigenteDesde)) e.push('La fecha no es válida.');
  else if (v.vigenteDesde < rango.min || v.vigenteDesde > rango.max) {
    e.push(`La fecha debe estar entre el ${fechaCorta(rango.min)} y el ${fechaCorta(rango.max)}.`);
  }
  if ((v.supervisor ?? '').length > NOMBRE_MAX) e.push(`El supervisor no puede pasar de ${NOMBRE_MAX} caracteres.`);
  if ((v.regional ?? '').length > NOMBRE_MAX) e.push(`El regional no puede pasar de ${NOMBRE_MAX} caracteres.`);
  if (v.motivo.length > MOTIVO_MAX) e.push(`El motivo no puede pasar de ${MOTIVO_MAX} caracteres.`);
  return e;
}
